import { Injectable, NgZone, inject } from '@angular/core';
import {
  getDocument,
  GlobalWorkerOptions,
  PasswordResponses,
  type OnProgressParameters,
  type PDFDocumentLoadingTask,
  type PDFDocumentProxy,
  type PDFPageProxy,
  type RenderTask,
} from 'pdfjs-dist';
import {
  PdfDocument,
  PdfPage,
  PdfRotation,
  PdfSource,
  PdfViewerError,
} from '../models/pdf-viewer.model';

/** Options for a single load. */
export interface PdfLoadOptions {
  fileName?: string;
  password?: string;
  /** Reports download progress as a fraction in `[0, 1]`. */
  onProgress?: (fraction: number) => void;
}

let workerConfigured = false;

/**
 * Thin, instance-scoped wrapper around PDF.js. It owns exactly one document at a time, caches
 * page proxies, renders to caller-supplied canvases (so the component keeps DOM ownership), and
 * guarantees cleanup. Provided in the component's `providers` array — never `root` — so each
 * viewer disposes its own `PDFDocumentProxy` on destroy.
 */
@Injectable()
export class PdfViewerService {
  private readonly zone = inject(NgZone);

  private loadingTask: PDFDocumentLoadingTask | null = null;
  private doc: PDFDocumentProxy | null = null;
  private readonly pageCache = new Map<number, Promise<PDFPageProxy>>();
  private readonly textCache = new Map<number, Promise<string>>();
  private rawData: Uint8Array | null = null;

  /** One-time global worker wiring. Safe to call repeatedly. */
  configureWorker(workerSrc: string): void {
    if (!workerConfigured && !GlobalWorkerOptions.workerSrc) {
      GlobalWorkerOptions.workerSrc = workerSrc;
      workerConfigured = true;
    }
  }

  /** Load a document from any supported source. Rejects with a {@link PdfViewerError}. */
  async load(source: PdfSource, options: PdfLoadOptions = {}): Promise<PdfDocument> {
    await this.destroy();

    let parameters: { url: string } | { data: Uint8Array };
    try {
      parameters = await this.toDocumentParameters(source);
    } catch (cause) {
      throw this.error('invalid-source', 'The provided PDF source could not be read.', cause);
    }

    // PDF.js schedules work outside Angular; running the load off the zone avoids needless CD churn.
    return this.zone.runOutsideAngular(async () => {
      const task = getDocument({ ...parameters, password: options.password });
      this.loadingTask = task;

      let passwordAttempted = false;
      let passwordReason = 0;
      task.onPassword = (updatePassword: (pwd: string) => void, reason: number) => {
        passwordReason = reason;
        if (reason === PasswordResponses.NEED_PASSWORD && options.password && !passwordAttempted) {
          passwordAttempted = true;
          updatePassword(options.password);
          return;
        }
        // Supplying an empty password yields a deterministic PasswordException rejection.
        updatePassword('');
      };

      if (options.onProgress) {
        task.onProgress = ({ loaded, total }: OnProgressParameters) => {
          options.onProgress?.(total > 0 ? loaded / total : 0);
        };
      }

      try {
        const doc = await task.promise;
        this.doc = doc;
        return await this.toDocumentInfo(doc, options.fileName, parameters, passwordReason !== 0);
      } catch (cause) {
        throw this.normalizeLoadError(cause, passwordReason);
      }
    });
  }

  /** Total pages in the loaded document (0 when none). */
  get pageCount(): number {
    return this.doc?.numPages ?? 0;
  }

  /** Resolve (and cache) a page proxy by 1-based number. */
  getPage(pageNumber: number): Promise<PDFPageProxy> {
    if (!this.doc) {
      return Promise.reject(new Error('No document is loaded.'));
    }
    const cached = this.pageCache.get(pageNumber);
    if (cached) {
      return cached;
    }
    const promise = this.doc.getPage(pageNumber);
    this.pageCache.set(pageNumber, promise);
    return promise;
  }

  /** Intrinsic geometry of a page at scale 1, rotation 0. */
  async getPageSize(pageNumber: number): Promise<PdfPage> {
    const page = await this.getPage(pageNumber);
    const viewport = page.getViewport({ scale: 1, rotation: 0 });
    return { pageNumber, width: viewport.width, height: viewport.height };
  }

  /**
   * Render a page onto a caller-owned canvas at the given zoom scale and rotation. Returns the
   * PDF.js {@link RenderTask} so the caller can cancel an in-flight render before starting another.
   */
  async renderPage(
    pageNumber: number,
    canvas: HTMLCanvasElement,
    scale: number,
    rotation: PdfRotation,
    devicePixelRatio: number
  ): Promise<RenderTask> {
    const page = await this.getPage(pageNumber);
    const viewport = page.getViewport({ scale, rotation });
    const context = canvas.getContext('2d');
    if (!context) {
      throw this.error('render', 'Could not acquire a 2D canvas context.');
    }

    const dpr = Math.max(1, devicePixelRatio);
    canvas.width = Math.floor(viewport.width * dpr);
    canvas.height = Math.floor(viewport.height * dpr);
    canvas.style.width = `${Math.floor(viewport.width)}px`;
    canvas.style.height = `${Math.floor(viewport.height)}px`;

    const task = page.render({
      canvasContext: context,
      viewport,
      transform: dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : undefined,
    });
    return task;
  }

  /** Plain text of a page, cached — used by search. */
  getText(pageNumber: number): Promise<string> {
    const cached = this.textCache.get(pageNumber);
    if (cached) {
      return cached;
    }
    const promise = this.getPage(pageNumber)
      .then((page) => page.getTextContent())
      .then((content) =>
        content.items
          .map((item) => ('str' in item ? item.str : ''))
          .join(' ')
          .replace(/\s+/g, ' ')
      );
    this.textCache.set(pageNumber, promise);
    return promise;
  }

  /** The raw bytes of the document, when it was loaded from binary data (for download/print). */
  getRawData(): Uint8Array | null {
    return this.rawData;
  }

  /** Dispose the current document and all caches. Idempotent. */
  async destroy(): Promise<void> {
    this.pageCache.clear();
    this.textCache.clear();
    // Reset here (not just in toDocumentParameters) so a subsequent URL load — which never sets
    // rawData — cannot serve the previous binary document's bytes to download/print.
    this.rawData = null;
    const task = this.loadingTask;
    const doc = this.doc;
    this.loadingTask = null;
    this.doc = null;
    try {
      await doc?.destroy();
    } catch {
      /* already destroyed */
    }
    try {
      await task?.destroy();
    } catch {
      /* already destroyed */
    }
  }

  private async toDocumentParameters(
    source: PdfSource
  ): Promise<{ url: string } | { data: Uint8Array }> {
    if (typeof source === 'string') {
      const base64 = this.extractBase64(source);
      if (base64 !== null) {
        this.rawData = this.base64ToBytes(base64);
        return { data: this.rawData };
      }
      return { url: source };
    }
    if (source instanceof Blob) {
      this.rawData = new Uint8Array(await source.arrayBuffer());
      return { data: this.rawData };
    }
    if (source instanceof ArrayBuffer) {
      this.rawData = new Uint8Array(source.slice(0));
      return { data: this.rawData };
    }
    if (source instanceof Uint8Array) {
      // Copy so PDF.js does not detach the caller's buffer.
      this.rawData = new Uint8Array(source);
      return { data: this.rawData };
    }
    throw new Error('Unsupported PDF source type.');
  }

  /** Returns the base64 payload if `value` is a data-URI or bare base64; otherwise null (a URL). */
  private extractBase64(value: string): string | null {
    const dataUri = /^data:application\/(?:pdf|octet-stream);base64,(.*)$/s.exec(value.trim());
    if (dataUri) {
      return dataUri[1];
    }
    const trimmed = value.trim();
    const looksLikeBase64 =
      trimmed.length > 100 && /^[A-Za-z0-9+/=\r\n]+$/.test(trimmed) && !/^https?:|^\//.test(trimmed);
    return looksLikeBase64 ? trimmed : null;
  }

  private base64ToBytes(base64: string): Uint8Array {
    const binary = atob(base64.replace(/\s/g, ''));
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }

  private async toDocumentInfo(
    doc: PDFDocumentProxy,
    fileName: string | undefined,
    parameters: { url: string } | { data: Uint8Array },
    encrypted: boolean
  ): Promise<PdfDocument> {
    let title: string | undefined;
    let author: string | undefined;
    let pdfVersion: string | undefined;
    try {
      const meta = await doc.getMetadata();
      const info = meta.info as { Title?: string; Author?: string; PDFFormatVersion?: string };
      // Keep `||` (not `??`): empty metadata strings must be normalised to undefined, so the
      // falsy '' case is intentionally treated as absent.
      title = info?.Title || undefined; // NOSONAR S6606 — intentional `||` (see comment above)
      author = info?.Author || undefined; // NOSONAR S6606 — intentional `||` (see comment above)
      pdfVersion = info?.PDFFormatVersion || undefined; // NOSONAR S6606 — intentional `||` (see comment above)
    } catch {
      /* metadata is optional */
    }
    return {
      pageCount: doc.numPages,
      // Keep `||` (not `??`): an empty-string fileName must fall back to a derived name, so the
      // falsy '' case is intentionally treated as absent.
      fileName: fileName || this.deriveFileName(parameters, title), // NOSONAR S6606 — intentional `||` (see comment above)
      title,
      author,
      pdfVersion,
      encrypted,
    };
  }

  private deriveFileName(
    parameters: { url: string } | { data: Uint8Array },
    title: string | undefined
  ): string {
    if ('url' in parameters) {
      const last = parameters.url.split(/[?#]/)[0].split('/').pop();
      if (last) {
        return decodeURIComponent(last);
      }
    }
    return title ? `${title}.pdf` : 'document.pdf';
  }

  private normalizeLoadError(cause: unknown, passwordReason = 0): PdfViewerError {
    if (this.isPdfViewerError(cause)) {
      return cause;
    }
    const name = (cause as { name?: string })?.name ?? '';
    const message = (cause as { message?: string })?.message ?? 'Failed to load the document.';
    if (name === 'PasswordException') {
      return passwordReason === PasswordResponses.INCORRECT_PASSWORD
        ? this.error('invalid-password', 'The password is incorrect.', cause)
        : this.error('password-required', 'This document is password protected.', cause);
    }
    if (name === 'InvalidPDFException') {
      return this.error('corrupt', 'The file is not a valid PDF.', cause);
    }
    if (name === 'MissingPDFException') {
      return this.error('network', 'The PDF could not be found.', cause);
    }
    if (name === 'UnexpectedResponseException') {
      return this.error('network', 'The server returned an unexpected response.', cause);
    }
    return this.error('unknown', message, cause);
  }

  private isPdfViewerError(value: unknown): value is PdfViewerError {
    return (
      typeof value === 'object' &&
      value !== null &&
      'code' in value &&
      'message' in value &&
      typeof (value as PdfViewerError).code === 'string'
    );
  }

  private error(
    code: PdfViewerError['code'],
    message: string,
    cause?: unknown
  ): PdfViewerError {
    return { code, message, cause };
  }
}
